<script setup lang="js">
import axios from "axios";
import { computed, defineProps, getCurrentInstance, onMounted, ref, watch } from "vue";
import { Formatter } from "@/utilities/Formatter";

const props = defineProps({
  start: {
    type: String,
    required: false,
    default: null
  },
  end: {
    type: String,
    required: false,
    default: null
  },
  resultData: {
    type: Object,
    required: false,
    default: null
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  error: {
    type: Boolean,
    required: false,
    default: false
  }
});

const localResult = ref(null);
const localLoading = ref(false);
const localError = ref(false);
const { proxy } = getCurrentInstance();
const t = (key) => proxy.$filters.localize(key);

const isExternal = computed(() => props.resultData !== null || (!props.start && !props.end));
const result = computed(() => (isExternal.value ? props.resultData : localResult.value));
const loading = computed(() => (isExternal.value ? props.loading : localLoading.value));
const hasError = computed(() => (isExternal.value ? props.error : localError.value));

const channelNames = computed(() => ({
  Drop: t("Dashboard.channels.drop"),
  TikTok: "TikTok",
  Instagram: "Instagram",
  Site: t("Dashboard.channels.site"),
  "Site Target": t("Dashboard.channels.siteTarget"),
  Facebook: "Facebook",
  "Without Source": t("Dashboard.channels.withoutSource")
}));

const channelOrder = ["Drop", "TikTok", "Instagram", "Site", "Site Target", "Facebook", "Without Source"];

const load = async () => {
  if (isExternal.value) {
    return;
  }

  try {
    localResult.value = null;
    localError.value = false;
    localLoading.value = true;
    const response = await axios.get(
      `${window.location.origin}/ui/v3/dashboard/success?start=${props.start}&end=${props.end ?? ""}`
    );
    localResult.value = response.data;
  } catch {
    localError.value = true;
  } finally {
    localLoading.value = false;
  }
};

const cards = computed(() => {
  if (!result.value) {
    return [];
  }

  return [
    { label: t("Dashboard.metrics.turnover"), value: Formatter.toCurrency(result.value.turnoverTotal) },
    { label: t("Dashboard.metrics.profit"), value: Formatter.toCurrency(result.value.profitTotal) },
    { label: t("Dashboard.metrics.averageCheckFull"), value: Formatter.toCurrency(result.value.averageCheck) },
    {
      label: t("Dashboard.metrics.averageMargin"),
      value: `${Formatter.toCurrency(result.value.averageMargin)} (${Number(result.value.marginPercent ?? 0).toFixed(2)}%)`
    }
  ];
});

const channels = computed(() => {
  const source = result.value?.channels ?? [];
  const byChannel = Object.fromEntries(source.map((item) => [item.channel, item]));

  return channelOrder.map((channel) => {
    const item = byChannel[channel];

    return {
      channel,
      displayName: channelNames.value[channel] ?? channel,
      turnoverTotal: item?.turnoverTotal ?? 0,
      turnoverPaid: item?.turnoverPaid ?? 0,
      profit: item?.profit ?? 0,
      purchaseAmount: item?.purchaseAmount ?? 0,
      averageCheck: item?.averageCheck ?? 0,
      averageMargin: item?.averageMargin ?? 0,
      marginPercent: item?.marginPercent ?? 0,
      count: item?.count ?? 0,
      retailCount: item?.retailCount ?? 0
    };
  });
});

onMounted(load);
watch(() => props.start, load);
watch(() => props.end, load);
</script>

<template>
  <section class="sales-overview">
    <div class="sales-overview__summary">
      <div class="sales-overview__summary-text">
        <h2 class="sales-overview__title">{{ $filters.localize("Dashboard.overview.title") }}</h2>
        <p class="sales-overview__description">
          {{ $filters.localize("Dashboard.overview.text") }}
        </p>
      </div>

      <div class="sales-overview__meta" v-if="result">
        <span class="sales-overview__meta-pill">{{ result.count }} {{ $filters.localize("Dashboard.overview.ordersCount") }}</span>
        <span class="sales-overview__meta-pill">{{ result.retailCount }} {{ $filters.localize("Dashboard.overview.retailCount") }}</span>
        <span class="sales-overview__meta-pill">{{ result.wholesaleCount ?? 0 }} {{ $filters.localize("Dashboard.overview.wholesaleCount") }}</span>
        <span class="sales-overview__meta-pill">{{ result.tailoringCount ?? 0 }} {{ $filters.localize("Dashboard.overview.tailoringCount") }}</span>
      </div>
    </div>

    <div v-if="result" class="sales-overview__content">
      <div class="sales-overview__cards">
        <article v-for="card in cards" :key="card.label" class="sales-overview__card">
          <div class="sales-overview__card-label">{{ card.label }}</div>
          <div class="sales-overview__card-value">{{ card.value }}</div>
        </article>
      </div>

      <div class="sales-overview__table-wrap">
        <table class="sales-overview__table">
          <thead>
            <tr>
              <th>{{ $filters.localize("Dashboard.table.channel") }}</th>
              <th>{{ $filters.localize("Dashboard.table.orders") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.turnover") }}</th>
              <th>{{ $filters.localize("Dashboard.table.received") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.purchase") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.profit") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.averageCheckFull") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.averageMarginShort") }}</th>
              <th>{{ $filters.localize("Dashboard.metrics.marginPercent") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="channel in channels" :key="channel.channel">
              <td>{{ channel.displayName }}</td>
              <td>{{ channel.count }}</td>
              <td>{{ Formatter.toCurrency(channel.turnoverTotal) }}</td>
              <td>{{ Formatter.toCurrency(channel.turnoverPaid) }}</td>
              <td>{{ Formatter.toCurrency(channel.purchaseAmount) }}</td>
              <td>{{ Formatter.toCurrency(channel.profit) }}</td>
              <td>{{ Formatter.toCurrency(channel.averageCheck) }}</td>
              <td>{{ Formatter.toCurrency(channel.averageMargin) }}</td>
              <td>{{ Number(channel.marginPercent ?? 0).toFixed(2) }}%</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="sales-overview__total-row">
              <td>{{ $filters.localize("Dashboard.table.total") }}</td>
              <td>{{ result.count }}</td>
              <td>{{ Formatter.toCurrency(result.turnoverTotal) }}</td>
              <td>{{ Formatter.toCurrency(result.turnoverRetailDrop) }}</td>
              <td>{{ Formatter.toCurrency(result.purchaseAmount) }}</td>
              <td>{{ Formatter.toCurrency(result.profitTotal) }}</td>
              <td>{{ Formatter.toCurrency(result.averageCheck) }}</td>
              <td>{{ Formatter.toCurrency(result.averageMargin) }}</td>
              <td>{{ Number(result.marginPercent ?? 0).toFixed(2) }}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-else-if="hasError" class="dashboard-state dashboard-state--error">
      {{ $filters.localize("Dashboard.states.overviewError") }}
    </div>

    <div v-else class="sales-overview__content">
      <div class="dashboard-skeleton__summary">
        <div class="dashboard-skeleton__line dashboard-skeleton__line--title"></div>
        <div class="dashboard-skeleton__line dashboard-skeleton__line--text"></div>
      </div>
      <div class="sales-overview__cards">
        <div v-for="index in 4" :key="index" class="dashboard-skeleton__tile dashboard-skeleton__tile--card"></div>
      </div>
      <div class="dashboard-skeleton__table">
        <div v-for="index in 6" :key="`row-${index}`" class="dashboard-skeleton__row"></div>
      </div>
    </div>

    <div v-if="loading && result" class="dashboard-loader-overlay">
      <div class="dashboard-loader"></div>
    </div>
  </section>
</template>

<style scoped>
.sales-overview {
  position: relative;
  min-height: 540px;
  padding: 1.5rem;
  border: 1px solid rgba(23, 50, 77, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 22px 60px rgba(23, 50, 77, 0.08);
  overflow: hidden;
}

.sales-overview__summary {
  display: flex;
  justify-content: space-between;
  gap: 1.25rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.sales-overview__summary-text {
  max-width: 52rem;
}

.sales-overview__title {
  margin: 0;
  color: #17324d;
  font-size: 1.55rem;
  line-height: 1.15;
}

.sales-overview__description {
  margin: 0.45rem 0 0;
  color: #6f8194;
  font-size: 0.95rem;
  line-height: 1.5;
}

.sales-overview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
}

.sales-overview__meta-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.65rem 1rem;
  border: 1px solid rgba(23, 50, 77, 0.08);
  border-radius: 999px;
  background: #f8fbfe;
  color: #6f8194;
  font-weight: 600;
  white-space: nowrap;
}

.sales-overview__content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.sales-overview__cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.sales-overview__card {
  padding: 1.15rem;
  border-radius: 22px;
  background: #f8fbfe;
  border: 1px solid rgba(23, 50, 77, 0.08);
}

.sales-overview__card-label {
  color: #6f8194;
  font-size: 0.9rem;
  margin-bottom: 0.45rem;
}

.sales-overview__card-value {
  color: #17324d;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.sales-overview__table-wrap {
  overflow-x: auto;
}

.sales-overview__table {
  width: 100%;
  min-width: 920px;
  border-collapse: separate;
  border-spacing: 0;
}

.sales-overview__table th,
.sales-overview__table td {
  padding: 0.95rem 0.85rem;
  border-bottom: 1px solid rgba(23, 50, 77, 0.08);
  text-align: left;
  white-space: nowrap;
}

.sales-overview__table th {
  color: #6f8194;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.sales-overview__table td {
  color: #17324d;
  font-weight: 500;
}

.sales-overview__total-row td {
  border-bottom: 0;
  border-top: 2px solid rgba(23, 50, 77, 0.12);
  background: #f5f9fd;
  color: #17324d;
  font-weight: 700;
}

.sales-overview__total-row td:first-child {
  color: #0f2d49;
}

.dashboard-state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  border-radius: 22px;
  text-align: center;
  font-weight: 600;
}

.dashboard-state--error {
  border: 1px dashed rgba(138, 91, 91, 0.24);
  background: rgba(255, 246, 246, 0.92);
  color: #8a5b5b;
}

.dashboard-skeleton__summary,
.dashboard-skeleton__table,
.dashboard-skeleton__line,
.dashboard-skeleton__tile,
.dashboard-skeleton__row {
  display: block;
}

.dashboard-skeleton__line,
.dashboard-skeleton__tile,
.dashboard-skeleton__row {
  background: linear-gradient(90deg, #edf4fb 0%, #f8fbfe 50%, #edf4fb 100%);
  background-size: 200% 100%;
  animation: dashboard-shimmer 1.5s infinite linear;
  border: 1px solid rgba(23, 50, 77, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.dashboard-skeleton__line {
  height: 14px;
  border-radius: 999px;
}

.dashboard-skeleton__line--title {
  width: 34%;
  height: 24px;
}

.dashboard-skeleton__line--text {
  width: 52%;
  margin-top: 0.85rem;
}

.dashboard-skeleton__tile--card {
  min-height: 112px;
  border-radius: 22px;
}

.dashboard-skeleton__table {
  display: grid;
  gap: 0.65rem;
}

.dashboard-skeleton__row {
  height: 52px;
  border-radius: 18px;
}

.dashboard-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(2px);
}

.dashboard-loader {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid rgba(23, 50, 77, 0.12);
  border-top-color: #17324d;
  animation: dashboard-spin 0.8s linear infinite;
}

@keyframes dashboard-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@keyframes dashboard-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .sales-overview__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .sales-overview {
    min-height: 0;
    padding: 0.75rem;
    border-radius: 14px;
  }

  .sales-overview__summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.65rem;
    margin-bottom: 0.8rem;
  }

  .sales-overview__title {
    font-size: 1.05rem;
  }

  .sales-overview__description {
    display: none;
  }

  .sales-overview__meta {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem;
  }

  .sales-overview__meta-pill {
    width: 100%;
    min-height: 30px;
    padding: 0.35rem 0.45rem;
    border-radius: 8px;
    font-size: 0.68rem;
    line-height: 1.15;
    white-space: normal;
    text-align: center;
  }

  .sales-overview__cards {
    grid-template-columns: 1fr;
  }
}
</style>
